// SnowTrack™ plan catalogue for the app UI (Settings, trial upgrade prompt).
//
// The marketing page (pages/Pricing.js) keeps its own TIERS array because it
// renders full per-plan feature lists that only make sense there, but takes its
// prices from here, as do the landing page plans strip and the landing page's
// JSON-LD. This file is the short form the in-app screens need: what a plan is
// called, what it costs, and whether a customer can buy it themselves.
//
// Prices are whole dollars for display. server/config/plans.js holds the
// canonical figures in cents and is what Stripe actually charges; if the two
// ever disagree, the server is right.
//
// The same goes for `limits`, which mirrors server/config/planLimits.js. This
// copy is for DISPLAY ONLY - it decides what a card says, never what a company
// may do. Every limit is enforced server-side on create, and a client that
// disagrees with the server simply shows the wrong number until it is fixed.
// null means unlimited.

export const PLANS = [
  {
    id: 'basic',
    name: 'Basic',
    price: 10,
    description: 'For the solo operator',
    limits: { users: 1, customers: 25 },
    selfServe: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 25,
    description: 'For a small crew that bills its own customers',
    limits: { users: 3, customers: 75 },
    selfServe: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 49,
    description: 'For a full route list and a real team',
    limits: { users: 10, customers: 200 },
    selfServe: true,
  },
  {
    // Not self-serve on purpose: Enterprise is a conversation about onboarding,
    // integrations and a named account manager, so its CTA opens an email
    // rather than Stripe Checkout.
    id: 'enterprise',
    name: 'Enterprise',
    price: 99,
    description: 'For multi-crew operations',
    limits: { users: null, customers: null },
    selfServe: false,
  },
];

// "25" for a whole-dollar price, "29.50" otherwise, without the $ sign. The
// static prices are whole dollars; a live price (hooks/useLivePlans.js) need
// not be.
export const formatDollars = (price) => (Number.isInteger(price) ? String(price) : Number(price).toFixed(2));

export const getPlan = (planId) =>
  PLANS.find((plan) => plan.id === String(planId || '').toLowerCase()) || null;

export const getPlanName = (planId) => {
  const plan = getPlan(planId);
  return plan ? plan.name : 'Free';
};

// A lapsed or never-paid company resolves to 'free' and gets Basic limits: it
// keeps everything it has and can add nothing. Matches PLAN_LIMITS.free on the
// server, which is what actually decides.
const FREE_LIMITS = { users: 1, customers: 25 };

export const getPlanLimits = (planId) => {
  const plan = getPlan(planId);
  return plan ? plan.limits : FREE_LIMITS;
};

// "3" or "Unlimited". null is unlimited, everywhere.
export const formatLimit = (value) => (value === null || value === undefined ? 'Unlimited' : String(value));

// "2 of 3", or "2 of Unlimited" -> "2". Used by the Settings usage line.
export const formatUsage = (used, limit) =>
  (limit === null || limit === undefined ? `${used}` : `${used} of ${limit}`);
